import { useCallback, useEffect, useState } from "react";
import { DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CHANNELS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useCrmMeta } from "@/hooks/useCrmMeta";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/crm/Badges";
import { PipelineColumn } from "@/components/crm/pipeline/PipelineColumn";
import { CardBody } from "@/components/crm/pipeline/PipelineCard";
import { SCHEDULED, useStageGuard } from "@/hooks/useStageGuard";

const F = ({ value, onChange, all, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10 w-full rounded-xl bg-white sm:w-48" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{all}</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const moveLocal = (cols, lead, to) =>
  cols.map((c) => {
    if (c.stage === lead.stage) return { ...c, count: c.count - 1, leads: c.leads.filter((l) => l.id !== lead.id) };
    if (c.stage === to) return { ...c, count: c.count + 1, leads: [{ ...lead, stage: to }, ...c.leads] };
    return c;
  });

export default function PipelinePage() {
  const { user } = useAuth();
  const { services, sellers } = useCrmMeta();
  const [filters, setFilters] = useState({ service_id: "all", channel: "all", seller_id: "all" });
  const [columns, setColumns] = useState([]);
  const [active, setActive] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );

  const params = useCallback(() => Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "all")), [filters]);
  const load = useCallback(() => {
    api.get("/pipeline", { params: params() }).then((r) => setColumns(r.data.columns));
  }, [params]);
  const loadMore = async (stage) => {
    const col = columns.find((c) => c.stage === stage);
    const { data } = await api.get("/pipeline/column", { params: { ...params(), stage, skip: col.leads.length, limit: 30 } });
    setColumns((cols) => cols.map((c) => {
      if (c.stage !== stage) return c;
      const seen = new Set(c.leads.map((l) => l.id));
      return { ...c, count: data.count, leads: [...c.leads, ...data.leads.filter((l) => !seen.has(l.id))] };
    }));
  };
  useEffect(() => { load(); }, [load]);

  const { guard, dialog } = useStageGuard();
  const move = async (lead, to) => {
    if (!to || to === lead.stage) return;
    const snapshot = columns;
    setColumns((cols) => moveLocal(cols, lead, to));
    if (to === SCHEDULED) {
      try {
        const done = await guard(lead, {
          proceed: () => true,
          saved: () => toast.success(`${lead.full_name} → ${to}`),
          revert: () => setColumns(snapshot),
        });
        if (!done) return;
      } catch {
        setColumns(snapshot);
        toast.error("No se pudo mover el lead");
        return;
      }
    }
    try {
      await api.patch(`/leads/${lead.id}/stage`, { stage: to });
      toast.success(`${lead.full_name} → ${to}`);
    } catch {
      setColumns(snapshot);
      toast.error("No se pudo mover el lead");
    }
  };

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="pipeline-page">
      <PageHeader title="Pipeline" subtitle="Arrastra las tarjetas o usa “Mover a…”" />
      <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap" data-testid="pipeline-filters">
        <F value={filters.service_id} onChange={set("service_id")} all="Todos los servicios" options={services.map((s) => [s.id, s.name])} testid="pipeline-filter-service" />
        <F value={filters.channel} onChange={set("channel")} all="Todos los canales" options={CHANNELS.map((c) => [c, c])} testid="pipeline-filter-channel" />
        {user.role !== "vendedor" && (
          <F value={filters.seller_id} onChange={set("seller_id")} all="Todos los vendedores"
             options={[...sellers.map((s) => [s.id, s.name]), ["unassigned", "Sin asignar"]]} testid="pipeline-filter-seller" />
        )}
      </div>
      <DndContext sensors={sensors} onDragStart={(e) => setActive(e.active.data.current.lead)} onDragCancel={() => setActive(null)}
                  onDragEnd={(e) => { setActive(null); if (e.over) move(e.active.data.current.lead, e.over.id); }}>
        <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10" data-testid="pipeline-board">
          <div className="flex w-max gap-4">
            {columns.map((c) => <PipelineColumn key={c.stage} column={c} onMove={move} onLoadMore={loadMore} />)}
            {dialog}
          </div>
        </div>
        <DragOverlay>{active && <div className="w-[280px] rotate-2 rounded-xl border border-[#1E6FD9]/30 bg-white p-3.5 shadow-2xl"><CardBody lead={active} /></div>}</DragOverlay>
      </DndContext>
    </div>
  );
}
